import { useCallback, useMemo } from 'react'
import type { FontFamily } from '@/api/types/font-family'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { useRecentFontFamilies } from '@/contexts/recent-font-families-context'
import { useFonts } from '@/providers/font'

/**
 * The panel's single path for emitting a style update, plus the recent-typeface shortcuts it
 * feeds: the 3 most recently *applied* font families (not "every font used somewhere on the
 * spread" — that was the old "Use on Page" list, not part of the approved design). The list lives
 * in `RecentFontFamiliesProvider`, not here, so it survives the Type/Select tool panel swap when
 * the user exits a text frame.
 */
export function useStyleUpdate(
  disabled: boolean,
  onStyleUpdate: ((update: TextRunStyleUpdate) => void) | undefined
) {
  const { getFamilyById } = useFonts()
  const { recentFontFamilyIds, recordAppliedFontFamily } = useRecentFontFamilies()

  const emitStyleUpdate = useCallback(
    (update: TextRunStyleUpdate) => {
      if (disabled) return

      if (typeof update.fontFamilyId === 'number') {
        recordAppliedFontFamily(update.fontFamilyId)
      }

      onStyleUpdate?.(update)
    },
    [disabled, onStyleUpdate, recordAppliedFontFamily]
  )

  const recentFontFamilies = useMemo(
    () =>
      recentFontFamilyIds.map((id) => getFamilyById(id)).filter((f): f is FontFamily => f != null),
    [recentFontFamilyIds, getFamilyById]
  )

  return { emitStyleUpdate, recentFontFamilies }
}
